import { lazy, Suspense, type ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/cn";

// SyntaxHighlighter — lazy load (~100kb gzip)
const SyntaxHighlighter = lazy(() =>
  import("react-syntax-highlighter").then((m) => ({ default: m.Prism })),
);

const oneDarkPromise = import(
  "react-syntax-highlighter/dist/esm/styles/prism/one-dark"
).then((m) => m.default);

let oneDarkStyle: Record<string, React.CSSProperties> | null = null;
oneDarkPromise.then((s) => {
  oneDarkStyle = s as unknown as Record<string, React.CSSProperties>;
});

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Audio link callback: [02:30](audio:CALL_ID) → onAudioCite */
  onAudioCite?: (callId: string, ts: string) => void;
}

const AUDIO_RE = /^audio:([\w-]+)$/;

export function MarkdownRenderer({
  content,
  className,
  onAudioCite,
}: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        "prose-coach text-[var(--text-primary)] text-sm leading-relaxed",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ ...props }) => (
            <h1
              {...props}
              className="mt-4 mb-3 text-xl font-semibold text-[var(--text-primary)] first:mt-0"
            />
          ),
          h2: ({ ...props }) => (
            <h2
              {...props}
              className="mt-4 mb-2 text-base font-semibold text-[var(--text-primary)] first:mt-0"
            />
          ),
          h3: ({ ...props }) => (
            <h3
              {...props}
              className="mt-3 mb-1.5 text-sm font-semibold text-[var(--text-primary)] first:mt-0"
            />
          ),
          p: ({ ...props }) => (
            <p
              {...props}
              className="mb-3 last:mb-0 text-[var(--text-secondary)]"
            />
          ),
          ul: ({ ...props }) => (
            <ul
              {...props}
              className="mb-3 list-disc pl-5 space-y-1 text-[var(--text-secondary)] marker:text-[var(--text-muted)]"
            />
          ),
          ol: ({ ...props }) => (
            <ol
              {...props}
              className="mb-3 list-decimal pl-5 space-y-1 text-[var(--text-secondary)] marker:text-[var(--text-muted)]"
            />
          ),
          li: ({ ...props }) => <li {...props} className="leading-snug" />,
          strong: ({ ...props }) => (
            <strong
              {...props}
              className="font-semibold text-[var(--text-primary)]"
            />
          ),
          em: ({ ...props }) => (
            <em {...props} className="italic text-[var(--text-primary)]" />
          ),
          blockquote: ({ ...props }) => (
            <blockquote
              {...props}
              className="mb-3 border-l-2 pl-3 italic text-[var(--text-muted)]"
              style={{ borderColor: "var(--brand-primary)" }}
            />
          ),
          a: ({ href, children, ...props }) => {
            const audioMatch = href?.match(AUDIO_RE);
            if (audioMatch && onAudioCite) {
              const callId = audioMatch[1];
              return (
                <button
                  type="button"
                  onClick={() => onAudioCite(callId, String(children))}
                  className="inline-flex items-center gap-1 rounded bg-[var(--bg-elevated)] px-1.5 py-0 font-mono text-xs text-[var(--info)] hover:bg-[var(--brand-primary)] hover:text-white transition-colors"
                  {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                >
                  ▶ {children}
                </button>
              );
            }
            return (
              <a
                {...props}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--info)] underline hover:opacity-80"
              >
                {children}
              </a>
            );
          },
          code: ({
            inline,
            className: codeCn,
            children,
            ...props
          }: ComponentProps<"code"> & { inline?: boolean }) => {
            const match = /language-(\w+)/.exec(codeCn || "");
            const codeStr = String(children).replace(/\n$/, "");
            if (!inline && match) {
              return (
                <Suspense
                  fallback={
                    <pre className="mb-3 overflow-x-auto rounded-md bg-[var(--bg-elevated)] p-3 text-xs">
                      <code>{codeStr}</code>
                    </pre>
                  }
                >
                  <SyntaxHighlighter
                    language={match[1]}
                    style={oneDarkStyle ?? {}}
                    PreTag="div"
                    customStyle={{
                      margin: 0,
                      marginBottom: 12,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    {codeStr}
                  </SyntaxHighlighter>
                </Suspense>
              );
            }
            return (
              <code
                className="rounded bg-[var(--bg-elevated)] px-1.5 py-0.5 font-mono text-[12px] text-[var(--text-primary)]"
                {...props}
              >
                {children}
              </code>
            );
          },
          table: ({ ...props }) => (
            <div className="mb-3 overflow-x-auto rounded-md border border-[var(--border-subtle)]">
              <table
                {...props}
                className="w-full border-collapse text-xs text-left"
              />
            </div>
          ),
          thead: ({ ...props }) => (
            <thead {...props} className="bg-[var(--bg-elevated)]" />
          ),
          th: ({ ...props }) => (
            <th
              {...props}
              className="border-b border-[var(--border-subtle)] px-3 py-2 font-semibold text-[var(--text-primary)]"
            />
          ),
          td: ({ ...props }) => (
            <td
              {...props}
              className="border-b border-[var(--border-subtle)]/50 px-3 py-2 text-[var(--text-secondary)] last:border-0"
            />
          ),
          hr: ({ ...props }) => (
            <hr
              {...props}
              className="my-4 border-[var(--border-subtle)]"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
